
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
const PARAGRAPHS = [
    "Rentid was built by a team of industry experts with experience across rental, car sharing, peer to peer mobility, the gig economy, insurance, finance, operations, and technology.",
    "In 2020, our founder coined \u201cprivate and direct rentals\u201d to define a new category of rental commerce built around direct bookings, embedded insurance, and modern fleet operations.",
    "While building and connecting per day commercial auto insurance with rental management software, our team worked directly with some of the largest modern fleet operators in North America. Through that work, one challenge became increasingly clear: after insurance, one of the biggest barriers to direct rentals was determining whether an unknown driver represented good or bad risk.",
    "By 2022, Rentid was established, and our strategy and innovation team began solving that problem.",
    "Our first product, Rent ID, was built to help fleet operators identify risk before handing over the keys, filtering out bad risk while allowing qualified drivers to move through the rental process and generate good revenue.",
    "That is also where the name Rentid comes from.",
    "Identity is part of our DNA, but Rentid, pronounced \u201crented,\u201d also represents the outcome every operator is working toward. When your vehicle is rented, it is generating revenue. Our goal is to help make that revenue smarter, safer, and more predictable.",
    "Today, Rentid empowers operators with the essential workflows and technology needed to run a modern rental business.",
    "We built our platform around a simple belief: no two rental operators run their businesses exactly the same way. Every fleet has different customers, operating models, policies, risk tolerances, and workflows.",
    "That is why your technology should adapt to your business, not force your business to adapt to the technology.",
    "With Rentid Pro, operators receive hands on onboarding from a team of industry professionals who have worked inside the businesses, technologies, and operational challenges our customers face every day. We work with you to configure Rentid around the way your business operates.",
    "We will never ask you to rebuild your business around our platform.",
    "We are here to become the technology partner behind your rental back office, helping simplify operations, reduce risk, automate repetitive work, and create more opportunities to generate revenue.",
    "Building an industry is not easy. Running a rental business is not easy either.",
    "We work hard to make both simpler, more efficient, and more profitable.",
];
function AboutBody() {
    return (<div className="mx-auto flex max-w-2xl flex-col items-center gap-5 text-center">
      <div className="mb-4 flex flex-col items-center gap-4">
        <img src="/logos/rentid-wordmark-black.svg" alt="Rentid" className="h-9 w-auto"/>
        <img src="/images/inshur-preferred-partner-2026.webp" alt="INSHUR Preferred Partner 2026" className="h-auto w-32 select-none drop-shadow-md"/>
      </div>
      {PARAGRAPHS.map((text) => (<p key={text.slice(0, 32)} className="text-base leading-relaxed text-foreground/80 text-pretty">
          {text}
        </p>))}
      <div className="mt-4 flex flex-col items-center gap-2">
        <p className="text-base leading-relaxed text-foreground/80">We look forward to building with you.</p>
        <p className="font-heading text-2xl font-bold tracking-tight text-foreground">Welcome to Rentid.</p>
      </div>
    </div>);
}
export function AboutRentid() {
    const [open, setOpen] = useState(false);
    const trigger = (<button type="button" onClick={() => setOpen(true)} className="btn-pulse-dark inline-flex items-center justify-center rounded-full bg-white px-7 py-4 text-center text-base font-semibold text-black shadow-[inset_0_0_0_1.5px_#000] transition-colors duration-200 hover:bg-neutral-100 active:scale-95 sm:py-3.5 sm:text-sm">
      About Rentid
    </button>);
    return (<>
      {trigger}
      <AboutRentidDialog open={open} onOpenChange={setOpen}/>
    </>);
}
/* The v0 version uses shadcn's Dialog (desktop) and Drawer (phones), which this site does not have;
   this is a plain modal that does the same job: light panel, centred, scrollable, closes on the X,
   on Escape and on a click outside; locks the page scroll while open. */
export function AboutRentidDialog({ open, onOpenChange, }) {
    useEffect(() => {
        if (!open)
            return;
        const onKey = (e) => {
            if (e.key === "Escape")
                onOpenChange(false);
        };
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onOpenChange]);
    if (!open)
        return null;
    /* Rendered into <body>: the product rail that opens this dialog is a transformed, fixed
       element, and a fixed dialog inside it would be positioned against the rail, not the
       viewport (owner 2026-10-01: "shows a tiny little box on the left side"). The .rd-home wrapper
       keeps the scoped Tailwind utilities applying outside the page tree. */
    return createPortal(<div className="rd-home"><div role="dialog" aria-modal="true" aria-label="About Rentid" className="fixed inset-0 z-[1100] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={() => onOpenChange(false)}>
      <div className="relative max-h-[90vh] w-full overflow-y-auto rounded-t-3xl bg-white px-6 pb-10 pt-12 text-[#171717] shadow-2xl sm:max-h-[88vh] sm:max-w-4xl sm:rounded-3xl sm:p-10 md:p-14" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => onOpenChange(false)} aria-label="Close" className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full bg-black/5 text-[#171717] outline-none transition-colors hover:bg-black/10 focus-visible:ring-2 focus-visible:ring-black/40">
          <X className="h-4 w-4" aria-hidden="true"/>
        </button>
        <AboutBody />
      </div>
    </div></div>, document.body);
}
